import { Print } from '@styled-icons/material';
import type { ParsedUrlQuery } from 'querystring';
import type * as React from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import tw from 'twin.macro';

import { Menu, NeedlMenuItem, NeedlMenuPopover } from '@/atoms/Menu/RadixMenu';
import type { TagElementType } from '@/atoms/Tags/Tags';
import { Tooltip } from '@/atoms/Tooltip/Tooltip';
import { Button } from '@/components/Button/Button';
import { DeleteMenuButton } from '@/components/DeleteMenu';
import type { IconButtonType } from '@/components/IconButton/IconButton';
import { IconButton } from '@/components/IconButton/IconButton';
import {
  commonDocumentTags,
  DeleteTooltip,
  DownloadTooltip,
  QuickRemovalTooltip,
} from '@/components/SourceCards/common';
import { Tags } from '@/components/UserTags';
import { useToggleAskNeedl } from '@/features/ChatBotV2/hooks/useToggleAskNeedl';
import { useGetFeedCardStatus } from '@/features/CustomFeedV2/hooks/useGetFeedCardStatus';
import { useUpdateDeeplinkReadStatus } from '@/features/CustomFeedV2/hooks/useUpdateFeedCardStatus';
import { useFeatureConfigUI } from '@/hooks/query/useFeatureConfig';
import { useResponsiveConstants } from '@/hooks/responsive/useResponsiveConstants';
import { usePreview } from '@/hooks/usePreview';
import { useShowDelete } from '@/hooks/useShowDelete';
import { useShowOpenInSource } from '@/hooks/useShowOpenInSource';
import ArrowLeft from '@/icons/arrow_left.svg?react';
import BackIcon from '@/icons/arrow-left-2.svg?react';
import CardReadIcon from '@/icons/card-read.svg?react';
import CardUnreadIcon from '@/icons/card-unread.svg?react';
import CutIcon from '@/icons/cut.svg?react';
import DeleteIcon from '@/icons/delete.svg?react';
import DownloadIcon from '@/icons/download-bracket.svg?react';
import ExternalLinkIcon from '@/icons/external-link.svg?react';
import QuickRemovalIcon from '@/icons/eye-off.svg?react';
import EditIcon from '@/icons/pencil-ui.svg?react';
import ShareIcon from '@/icons/share.svg?react';
import OpenInSourceIcon from '@/icons/web.svg?react';
import { useNavigate } from '@/router/hooks/useNavigate';
import type { FeedItemType } from '@/types';
import { filterTags } from '@/utils/filterTags';
import { getLogo } from '@/utils/getIcons';

const DeleteMenu = ({
  handleDelete,
  selectedItemsCount,
  group_name,
  handleDeleteAll,
}: DeleteMenuProps) => {
  const { shouldShowDelete } = useShowDelete();
  return shouldShowDelete ? (
    <Menu>
      <DeleteTooltip>
        <DeleteMenuButton selectedItemsCount={selectedItemsCount} />
      </DeleteTooltip>
      <NeedlMenuPopover>
        <NeedlMenuItem
          onClick={handleDelete}
          disabled={(selectedItemsCount ?? 0) < 1}
        >
          Delete Selected Message(s)
        </NeedlMenuItem>
        <NeedlMenuItem onClick={handleDeleteAll}>
          Delete {group_name ? 'Entire Chat' : 'Group'}
        </NeedlMenuItem>
      </NeedlMenuPopover>
    </Menu>
  ) : null;
};

const QuickRemovalButton = ({
  handleClick,
}: {
  handleClick: React.MouseEventHandler<HTMLButtonElement>;
}) => {
  return (
    <QuickRemovalTooltip>
      <IconButton size='small' onClick={handleClick}>
        <QuickRemovalIcon />
      </IconButton>
    </QuickRemovalTooltip>
  );
};

const DownloadButton: React.FunctionComponent<DownloadButtonType> = ({
  handleClick,
}) => {
  if (!handleClick) {
    return null;
  }

  return (
    <DownloadTooltip>
      <IconButton size='small' onClick={handleClick}>
        <DownloadIcon />
      </IconButton>
    </DownloadTooltip>
  );
};

const ShareButton: React.FunctionComponent<{
  handleShare: (e: React.MouseEvent) => void;
  disabled?: boolean;
  tooltipLabel?: string;
}> = ({ handleShare, disabled = false, tooltipLabel = 'Share' }) => (
  <Tooltip label={tooltipLabel}>
    <IconButton
      circular={true}
      onClick={handleShare}
      disabled={disabled}
      label='share button'
      size='small'
    >
      <ShareIcon />
    </IconButton>
  </Tooltip>
);

const CardStatusButton: React.FunctionComponent<{
  result: FeedItemType;
}> = ({ result }) => {
  const updateDeeplinkReadMutation = useUpdateDeeplinkReadStatus();
  const { isLoading, readStatus } = useGetFeedCardStatus(result);
  const tooltipLabel = readStatus ? 'Mark as Unread' : 'Mark as Read';

  const updateCardStatus = () => {
    const updateCardStatus = readStatus ? 'unread' : 'read';
    updateDeeplinkReadMutation.mutate({
      deeplink: result.deeplink,
      status: updateCardStatus,
    });
  };

  if (isLoading) {
    return null;
  }

  return (
    <Tooltip label={tooltipLabel}>
      <IconButton
        circular
        onClick={updateCardStatus}
        disabled={updateDeeplinkReadMutation.isLoading}
        label='card status button'
        size='small'
        fill={false}
      >
        {readStatus ? <CardReadIcon /> : <CardUnreadIcon />}
      </IconButton>
    </Tooltip>
  );
};

const EditButton: React.FunctionComponent<{
  handleEdit: (e: React.MouseEvent) => void;
  disabled?: boolean;
  size?: IconButtonType['size'];
}> = ({ handleEdit, disabled = false }) => (
  <Tooltip label='Edit'>
    <IconButton
      circular={true}
      background
      onClick={handleEdit}
      disabled={disabled}
      label='edit button'
      size='small'
    >
      <EditIcon />
    </IconButton>
  </Tooltip>
);

const DeleteButton: React.FunctionComponent<{
  handleDelete: (e: React.MouseEvent) => void;
  disabled?: boolean;
}> = ({ handleDelete, disabled = false }) => {
  const { shouldShowDelete } = useShowDelete();

  if (!handleDelete || !shouldShowDelete) {
    return null;
  }

  return (
    <Tooltip label='Delete'>
      <IconButton
        circular={true}
        onClick={handleDelete}
        disabled={disabled}
        label='delete button'
        size='small'
      >
        <DeleteIcon />
      </IconButton>
    </Tooltip>
  );
};

const ToggleAskNeedlButton = () => {
  const searchParams = new URLSearchParams(location.search);
  const { feature, isLoading } = useFeatureConfigUI('ask_needl_copilot');
  const { toggleAskNeedl, isOpen } = useToggleAskNeedl();
  const handleToggleAskNeedl = () => {
    toggleAskNeedl('preview');
  };

  const StarGradient = getLogo('ask_needl_star_gradient');

  const showToggleButton =
    searchParams.get('overlay-type') === 'preview' &&
    searchParams.get('overlay-source') !== 'needl_notebooks';

  if (!showToggleButton || isOpen || !feature || isLoading) {
    return null;
  }

  return (
    <Tooltip label='AskNeedl'>
      <IconButton
        circular={true}
        onClick={handleToggleAskNeedl}
        label='AskNeedl'
        size='small'
      >
        <StarGradient />
      </IconButton>
    </Tooltip>
  );
};

const PrintButton: React.FunctionComponent<{
  handlePrint: (e: React.MouseEvent) => void;
  disabled?: boolean;
}> = ({ handlePrint, disabled = false }) => (
  <Tooltip label='Print'>
    <IconButton
      circular={true}
      background
      onClick={handlePrint}
      disabled={disabled}
      label='print button'
      size='small'
    >
      <Print />
    </IconButton>
  </Tooltip>
);

const OpenInSource: React.FunctionComponent<{
  onClick: (e: React.MouseEvent) => void;
  channelId: string;
  result?: FeedItemType;
}> = ({ onClick, channelId, result }) => {
  const showButton = useShowOpenInSource({ channelId, result });

  return showButton ? (
    <Button
      variant='action'
      size='small'
      startIcon={<OpenInSourceIcon />}
      onClick={onClick}
    >
      Source
    </Button>
  ) : null;
};

const OpenInNewTab: React.FunctionComponent<{
  onClick: (e: React.MouseEvent) => void;
}> = ({ onClick }) => {
  return (
    <Button
      variant='action'
      size='small'
      className='bg-white'
      startIcon={<ExternalLinkIcon />}
      onClick={onClick}
    >
      New tab
    </Button>
  );
};

const ClipParagraphButton: React.FunctionComponent<{
  onClick: (e: React.MouseEvent) => void;
}> = ({ onClick }) => {
  return (
    <Button
      variant='action'
      size='small'
      startIcon={<CutIcon />}
      onClick={onClick}
    >
      Clip
    </Button>
  );
};

const BackButtonImpl: React.FunctionComponent<BackButtonType> = ({
  backHandler,
}) => (
  <Button
    variant='action'
    size='small'
    className='bg-white'
    startIcon={<ArrowLeft />}
    onClick={backHandler}
  >
    Back
  </Button>
);

const BackButton: React.FunctionComponent<BackButtonType> = ({
  backHandler,
}) => {
  const { mobile, desktop } = useResponsiveConstants();
  const navigate = useNavigate();
  const isPreview = usePreview();
  const history = useHistory();

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore : history.length is valid
  const isPreviewSameTab = isPreview && history?.length > 1;

  if (!isPreviewSameTab || mobile) {
    return null;
  }

  if (!desktop) {
    return (
      <IconButton size='large' onClick={() => navigate.goBack()}>
        <BackIcon className='text-gray-900' />
      </IconButton>
    );
  }

  return <BackButtonImpl backHandler={backHandler} />;
};

const getDocParams = (query: ParsedUrlQuery, data: FeedItemType) => {
  const searchTerm = query.q;
  const link = data.object_location || data.link_to_report;

  const params = {
    ...data,
    searchTerm,
    fileType: 'link',
    link,
  };

  return params;
};

const TagsSection = ({ data, tags, tagsLoading, source }: TagSectionType) => {
  return (
    <div className='flex gap-2'>
      <Tags
        tags={filterTags([
          ...commonDocumentTags(data),
          ...(!tagsLoading && tags && tags?.sharedTags?.length > 0
            ? tags.sharedTags.map<TagElementType>((tag) => ({
                value: tag,
                color: 'warn',
                type: 'shared_tags',
                source,
              }))
            : [{ value: '' }]),
          ...(!tagsLoading && tags && tags?.userTags?.length > 0
            ? tags.userTags.map<TagElementType>((tag) => ({
                value: tag,
                color: 'info',
                type: 'user_tags',
                source,
              }))
            : [{ value: '' }]),
        ])}
      />
    </div>
  );
};

type TagSectionType = {
  tags: {
    tags: string[];
    userTags: string[];
    sharedTags: string[];
  } | null;
  tagsLoading: boolean;
  data: FeedItemType;
  source: string;
};

type BackButtonType = {
  backHandler: () => void;
};

type DeleteMenuProps = {
  handleDelete: (e: React.MouseEvent) => void;
  selectedItemsCount: number;
  group_name?: string;
  handleDeleteAll: (e: React.MouseEvent) => void;
};

type DownloadButtonType = { handleClick: (e: React.MouseEvent) => void };

const PreviewsWrapper = styled.div`
  ${tw`flex overflow-hidden h-full`}
`;

const PreviewContentWrapper = styled.div`
  ${tw`overflow-auto flex-[2] md:w-3/5 lg:w-full`}
`;

const AskNeedlPreviewWrapper = styled.div`
  ${tw`flex-[1.2] lg:w-2/5 xl:w-auto`}
`;

export {
  BackButton,
  BackButtonImpl,
  ClipParagraphButton,
  DeleteButton,
  DeleteMenu,
  DownloadButton,
  EditButton,
  OpenInNewTab,
  OpenInSource,
  PrintButton,
  QuickRemovalButton,
  ShareButton,
  CardStatusButton,
  TagsSection,
  getDocParams,
  PreviewsWrapper,
  PreviewContentWrapper,
  ToggleAskNeedlButton,
  AskNeedlPreviewWrapper,
};
